import { Box, styled, TextField, Typography } from '@mui/material';
import ModalTemplate from 'ui/ModalTemplate/ModalTemplate';
import React, { useEffect, useState } from 'react';
import { AutocompletAble } from 'ui/DropdownItems/types';
import OrderedAutocompleteWithConfirm from 'ui/DropdownItems/OrderedAutocompleteWithConfirm';
import {
    BudgetProposalT,
    CellRendererParams,
} from 'routes/BudgetModule/config/types';
import VentilationConfirmationParagraph from './components/ConfirmationParagraph';
import { useIntegrationSoftwares } from 'helpers/integrations';
import { useTranslation } from 'react-i18next';
import { ProjectionVentilation } from 'routes/BudgetModule/components/BudgetProposal/Resource/Projection/helpers/types';
import { ProjectionTypes } from 'routes/Admin/Budget/BudgetManagement/BudgetList/CellRenderers/constants';

const StyHighlight = styled(Box, {
    name: 'StyHighlight',
})(({ theme }) => ({
    fontWeight: 700,
    color: theme.palette.primary.main,
}));

export type ProjectionVentilationRendererParamsT = CellRendererParams<
    BudgetProposalT,
    ProjectionVentilation.SoftwareIds[]
>;

type CommonVentilationRendererParamsT = {
    budgetProposal: BudgetProposalT;
    initialProjectionVentilations: ProjectionVentilation.SoftwareIds[];
    dropDownTitle: string;
    availableValues: AutocompletAble<ProjectionVentilation.SoftwareIds>[];
    handleVentilationsMutation: (
        projectionVentilation: ProjectionVentilation.SoftwareIds[],
    ) => void;
    projectionType: ProjectionTypes;
    sortOptions?: (
        options: AutocompletAble<ProjectionVentilation.SoftwareIds>[],
    ) => AutocompletAble<ProjectionVentilation.SoftwareIds>[];
};

/**
 * Given a list of projectionVentilations, returns a list of values compatibles with our AutoComplete component.
 */
const getAutoCompleteValuesFromVentilations = (
    projectionVentilations: ProjectionVentilation.SoftwareIds[],
    availableValues: AutocompletAble<ProjectionVentilation.SoftwareIds>[],
): AutocompletAble<ProjectionVentilation.SoftwareIds>[] =>
    projectionVentilations
        .map((v) => availableValues.find((aV) => aV.value === v)!)
        .filter((v) => v !== undefined);

export default function CommonProjectionVentilationsCellRenderer({
    budgetProposal,
    initialProjectionVentilations,
    dropDownTitle,
    availableValues,
    handleVentilationsMutation,
    projectionType,
    sortOptions,
}: CommonVentilationRendererParamsT): React.ReactElement {
    const [showConfirm, setShowConfirm] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const [selectedVentilations, setSelectedVentilations] = useState<
        AutocompletAble<ProjectionVentilation.SoftwareIds>[]
    >([]);
    const { t } = useTranslation();
    const confirm = t('common.confirm').toLowerCase();
    const modalTitle = t(`budgetProposal.confirm${projectionType}Title`);

    const { data: softwares } = useIntegrationSoftwares();

    const resetSelectedVentilations = () =>
        setSelectedVentilations(
            getAutoCompleteValuesFromVentilations(
                initialProjectionVentilations,
                availableValues,
            ),
        );

    const toggleConfirmModal = () => {
        setShowConfirm(!showConfirm);
    };

    const closeAndSubmitPhase = () => {
        handleVentilationsMutation(selectedVentilations.map((v) => v.value));
        toggleConfirmModal();
    };

    const handleClose = () => {
        if (!showConfirm) resetSelectedVentilations();
    };

    const handleCancel = () => {
        toggleConfirmModal();
        resetSelectedVentilations();
    };

    const handleConfirmationInputTextChange = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const keywordMatches =
            event.target.value.toLowerCase().trim() === confirm;
        setIsValid(keywordMatches);
    };

    const confirmationExplanationParagraph = (
        <Typography sx={{ mt: 2 }}>
            {t('budgetProposal.confirmationWordPart1')}{' '}
            <StyHighlight component="span">{confirm}</StyHighlight>{' '}
            {t('budgetProposal.confirmationWordPart2')}
        </Typography>
    );

    const checkSelectedVentilationsValidity = () =>
        selectedVentilations.length > 0 &&
        JSON.stringify(selectedVentilations.map((v) => v.value)) !==
            JSON.stringify(initialProjectionVentilations);

    useEffect(() => {
        resetSelectedVentilations();
    }, [
        JSON.stringify(initialProjectionVentilations),
        JSON.stringify(availableValues),
    ]);

    if (softwares === undefined) {
        return <></>;
    }

    const confirmationVentilationParagraph = (
        <VentilationConfirmationParagraph
            values={selectedVentilations}
            budgetProposal={budgetProposal}
            projectionType={projectionType}
            softwares={softwares}
        />
    );

    return (
        <>
            <OrderedAutocompleteWithConfirm
                selectedValues={selectedVentilations}
                availableValues={availableValues}
                setSelectedValues={setSelectedVentilations}
                label={dropDownTitle}
                onConfirm={() => setShowConfirm(true)}
                onClose={handleClose}
                sx={{ m: '2rem 0', width: '100%' }}
                confirmCondition={checkSelectedVentilationsValidity()}
                sortOptions={sortOptions}
            />
            {showConfirm && (
                <ModalTemplate
                    openModal={showConfirm}
                    onCloseModal={handleCancel}
                    onAction={closeAndSubmitPhase}
                    actionText={t('common.confirm')}
                    modalTitle={modalTitle}
                    disableActionButton={!isValid}
                >
                    {confirmationVentilationParagraph}
                    {confirmationExplanationParagraph}
                    <TextField
                        placeholder={confirm}
                        onChange={handleConfirmationInputTextChange}
                        size="small"
                        error={!isValid}
                        sx={{ mt: 2 }}
                    />
                </ModalTemplate>
            )}
        </>
    );
}
